import { Space } from 'interfaces/space';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { goToAllSpaces } from 'utils/routerHelpers';
import { canPostInReadOnlySpace } from 'utils/userHelpers';

import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import CommunityHeader from 'components/CommunityHeader/CommunityHeader';
import CreateNewPost from 'components/CreateNewPost';
import { getLayout } from 'components/Layouts/MainLayout';
import PostList from 'components/PostList';
import SpaceEmptyState from 'components/SpaceEmptyState/SpaceEmptyState';
import SpaceInfo from 'components/SpaceInfo/SpaceInfo';
import WelcomeToSpace from 'components/WelcomeToSpace';
import AvatarBanner from 'components/new/AvatarBanner/AvatarBanner';
import Layout from 'components/new/layout';

import { SPACE_INFO_QUERY } from 'schema/Space/queries';

interface SpacePageProps {
  postListState: {
    useCacheFirst: boolean;
  };
}

const SpacePostsPage = ({ postListState }: SpacePageProps) => {
  const { t } = useTranslation();
  const [space, setSpace] = useState<Space | null>(null);
  const { community, currentUser } = useCommunityContext();

  const router = useRouter();
  const { spaceId } = router.query;

  const { data } = useQuery(SPACE_INFO_QUERY, {
    variables: {
      communityId: community.id,
      spaceId,
    },
    notifyOnNetworkStatusChange: true,
    skip: !spaceId,
  });

  useEffect(() => {
    if (data?.community) {
      setSpace(data.community?.communitySpace as Space);
    }
  }, [data]);

  if (!space) {
    return <></>;
  }

  const pageTitle = t('pages-space_title', 'Space', { name: '' });

  return (
    <>
      <CommunityHeader pageTitle={pageTitle} />
      <Layout.ContextHeader
        breadcrumb={{
          url: `/communities/${community.id}/spaces`,
          onClick: (e) => {
            e.preventDefault();
            goToAllSpaces(router, community.id);
          },
        }}
        pageTitle={pageTitle}
      />
      <Layout.PageContent>
        <SpaceInfo space={space} />
        {spaceId && <WelcomeToSpace />}

        {(!space.readOnly || canPostInReadOnlySpace(currentUser)) && (
          <CreateNewPost selectedSpace={space} />
        )}
        {space.readOnly && !canPostInReadOnlySpace(currentUser) && (
          <AvatarBanner
            message={t(
              'pages-spaces-read_only_notification',
              'This space is currently set to read only. Only moderators can post.'
            )}
            user={currentUser}
          />
        )}
        <PostList
          currentSpaceId={space.id}
          customEmptyState={<SpaceEmptyState />}
          emptyMessage={t(
            'pages-spaces-post_list_empty_message',
            'Start a discussion by creating a new post from above.'
          )}
          emptyTitle={t("Looks like you'll be the first!")}
          key={space.id}
          sharedState={postListState}
          t={t}
        />
      </Layout.PageContent>
    </>
  );
};

SpacePostsPage.getLayout = getLayout;

export default SpacePostsPage;
